import React from "react";
import axios from "axios";
import catLogo from "../images/assistant_cat.png";
import "./Assistant.css";

const RAG_API_BASE =
  process.env.REACT_APP_RAG_API_BASE ||
  (window.location.hostname === "localhost"
    ? window.location.origin
    : "https://rag.ra.cs.uchicago.edu");

const REQUEST_TIMEOUT_MS = 12000;
const RETRY_DELAY_MS = 400;
const SUGGESTED_QUESTIONS = [
  "When is CSIL open?",
  "Where is CSIL located?",
  "What equipment can I use?",
  "How can a CSIL tutor help me?",
];

class Assistant extends React.Component {
  state = {
    messages: [],
    input: "",
    isLoading: false,
    error: null,
  };

  conversationRef = React.createRef();

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.messages.length !== this.state.messages.length ||
      prevState.isLoading !== this.state.isLoading
    ) {
      this.conversationRef.current?.scrollTo({
        top: this.conversationRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }

  handleChange = (event) => {
    this.setState({ input: event.target.value });
  };

  handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.handleSend();
    }
  };

  handleSuggestion = (question) => {
    if (!this.state.isLoading) {
      this.setState({ input: question }, this.handleSend);
    }
  };

  requestAnswer = async (question) => {
    let lastError;

    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        return await axios.post(
          `${RAG_API_BASE}/public/rag/query`,
          { question },
          {
            headers: { "Content-Type": "application/json" },
            timeout: REQUEST_TIMEOUT_MS,
          }
        );
      } catch (error) {
        lastError = error;
        const status = error.response && error.response.status;
        const canRetry = !status || status >= 500;

        if (!canRetry || attempt === 1) {
          throw error;
        }

        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      }
    }

    throw lastError;
  };

  handleSend = async () => {
    const text = this.state.input.trim();
    if (!text || this.state.isLoading) {
      return;
    }

    if (!RAG_API_BASE) {
      this.setState({
        error:
          "CSIL Assistant is not configured. Please set REACT_APP_RAG_API_BASE to your external RAG backend URL.",
      });
      return;
    }

    const userMessage = {
      id: Date.now().toString(),
      text,
      isUser: true,
    };

    const messages = [...this.state.messages, userMessage];
    this.setState({ messages, input: "", isLoading: true, error: null });

    try {
      const response = await this.requestAnswer(text);
      const answerText =
        response &&
        response.data &&
        typeof response.data.answer === "string"
          ? response.data.answer
          : "Sorry, I could not understand that response.";

      const botMessage = {
        id: (Date.now() + 1).toString(),
        text: answerText,
        isUser: false,
      };

      this.setState({
        messages: [...messages, botMessage],
        isLoading: false,
      });
    } catch (error) {
      console.error("Error contacting RAG backend:", error);
      const botMessage = {
        id: (Date.now() + 1).toString(),
        text: "I couldn't reach the CSIL Assistant just now. Try once more, or visit the CSIL tutor desk if you need help right away.",
        isUser: false,
      };
      this.setState({
        messages: [...messages, botMessage],
        isLoading: false,
        error: "The assistant could not connect to its knowledge service.",
      });
    }
  };

  renderText = (text) =>
    text.split(/(https?:\/\/[^\s]+)/g).map((part, index) =>
      /^https?:\/\//.test(part) ? (
        <a key={index} href={part} target="_blank" rel="noreferrer">
          {part.replace(/^https?:\/\//, "")}
        </a>
      ) : (
        <React.Fragment key={index}>{part}</React.Fragment>
      )
    );

  renderMessage = (message) => (
    <div
      className={`assistant-message-row ${
        message.isUser ? "assistant-message-row--user" : ""
      }`}
      key={message.id}
    >
      {!message.isUser && (
        <img
          className="assistant-message-avatar"
          src={catLogo}
          alt=""
          aria-hidden="true"
        />
      )}
      <div
        className={`assistant-message ${
          message.isUser ? "assistant-message--user" : "assistant-message--bot"
        }`}
      >
        <span className="assistant-message-label">
          {message.isUser ? "You" : "CSIL Assistant"}
        </span>
        <div className="assistant-message-text">
          {this.renderText(message.text)}
        </div>
      </div>
    </div>
  );

  render() {
    const { error, input, isLoading, messages } = this.state;
    const hasMessages = messages.length > 0;

    return (
      <main className="assistant-page">
        <section className="assistant-shell" aria-label="CSIL Assistant chat">
          <header className="assistant-header">
            <div className="assistant-heading">
              <img
                src={catLogo}
                className={`assistant-cat ${
                  isLoading ? "assistant-cat--thinking" : ""
                }`}
                alt="CSIL pixel cat"
              />
              <div>
                <p className="assistant-kicker">CSIL ASSISTANT</p>
                <h1>Ask CSIL</h1>
                <p className="assistant-intro">
                  Practical answers about the lab, equipment, services, and
                  getting help.
                </p>
              </div>
            </div>
          </header>

          <div
            className="assistant-conversation"
            aria-live="polite"
            ref={this.conversationRef}
          >
            {!hasMessages && (
              <div className="assistant-welcome">
                <h2>What can we help you find?</h2>
                <p>
                  Ask the kind of question you would bring to the front desk.
                  The assistant searches CSIL's public information and gives you
                  a direct answer.
                </p>
              </div>
            )}

            {messages.map(this.renderMessage)}

            {isLoading && (
              <div
                className="assistant-message-row assistant-thinking"
                role="status"
              >
                <img
                  className="assistant-message-avatar assistant-message-avatar--thinking"
                  src={catLogo}
                  alt=""
                  aria-hidden="true"
                />
                <div className="assistant-thinking-copy">
                  Looking through CSIL resources
                  <span className="assistant-thinking-dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="assistant-controls">
            <div
              className="assistant-suggestions"
              aria-label="Suggested questions"
            >
              {SUGGESTED_QUESTIONS.map((question) => (
                <button
                  type="button"
                  className="assistant-suggestion"
                  key={question}
                  onClick={() => this.handleSuggestion(question)}
                  disabled={isLoading}
                >
                  {question}
                </button>
              ))}
            </div>

            <div className="assistant-composer">
              <textarea
                value={input}
                onChange={this.handleChange}
                onKeyDown={this.handleKeyDown}
                placeholder="Ask a question about CSIL…"
                aria-label="Question for CSIL Assistant"
                maxLength="500"
                rows="1"
              />
              <button
                type="button"
                className="assistant-send"
                onClick={this.handleSend}
                disabled={isLoading || !input.trim()}
                aria-label="Send question"
              >
                <span>Send</span>
                <span aria-hidden="true">↗</span>
              </button>
            </div>

            {error && <p className="assistant-error">{error}</p>}
          </div>
        </section>
      </main>
    );
  }
}

export default Assistant;
